'use client';

import { useEffect, useState } from 'react';
import { m } from 'framer-motion';

import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Container from '@mui/material/Container';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

import { SimpleLayout } from 'src/layouts/simple';
import { varBounce, MotionContainer } from 'src/components/animate';
import { hämtaSenasteFel, type Felpost } from 'src/lib/fel/felbutik';
import { rapporteraKlientfel } from 'src/lib/fel/rapporteraKlientfel';

import { FelrapportDialog } from 'src/components/felrapport/FelrapportDialog';

// ----------------------------------------------------------------------

type Props = {
  error: Error & { digest?: string };
  reset?: () => void;
  title?: string;
  message?: string;
};

const ärUtveckling = process.env.NODE_ENV === 'development';

/**
 * Vad användaren möter när en vy har kraschat.
 *
 * Vyn visade tidigare en rå stacktrace på engelska. Det hjälpte utvecklaren som råkade sitta
 * framför skärmen och ingen annan — för en styrelseledamot i en bostadsrättsförening är det
 * obegripligt och oroande. Stacktracen går nu till servern i stället, och användaren får en
 * referens och ett erbjudande om att berätta vad som hände. I utvecklingsläge visas stacken
 * fortfarande, för då är det utvecklaren som tittar.
 */
export function CustomErrorView({
  error,
  reset,
  title = 'Något gick fel',
  message = 'Ett oväntat fel inträffade och sidan kunde inte visas. Felet har loggats.',
}: Props) {
  const [referens, setReferens] = useState<string | undefined>();
  const [dialogÖppen, setDialogÖppen] = useState(false);
  const [fel, setFel] = useState<Felpost | null>(null);

  useEffect(() => {
    let avbruten = false;
    rapporteraKlientfel(error).then((ref) => {
      if (!avbruten) {
        setReferens(ref);
        setFel(hämtaSenasteFel());
      }
    });
    return () => {
      avbruten = true;
    };
  }, [error]);

  return (
    <SimpleLayout slotProps={{ content: { compact: true } }}>
      <Container component={MotionContainer}>
        <m.div variants={varBounce('in')}>
          <Typography variant="h3" sx={{ mb: 2 }}>
            {title}
          </Typography>
        </m.div>

        <m.div variants={varBounce('in')}>
          <Typography sx={{ color: 'text.secondary' }}>{message}</Typography>
        </m.div>

        {referens && (
          <m.div variants={varBounce('in')}>
            <Alert severity="info" variant="outlined" sx={{ mt: 3, textAlign: 'left' }}>
              Referens: <strong>{referens}</strong> — uppge den om du kontaktar oss.
            </Alert>
          </m.div>
        )}

        {ärUtveckling && (
          <Box
            sx={{
              p: 2,
              mt: 4,
              width: '100%',
              borderRadius: 2,
              textAlign: 'left',
              bgcolor: 'background.neutral',
              border: (theme) => `dashed 1px ${theme.palette.divider}`,
              maxHeight: '50vh',
              overflow: 'auto',
            }}
          >
            <Typography variant="caption" sx={{ color: 'text.disabled' }}>
              Visas endast i utvecklingsläge
            </Typography>
            <Typography
              variant="subtitle2"
              color="error"
              sx={{ fontFamily: 'monospace', wordBreak: 'break-word', mt: 1 }}
            >
              {error.name}: {error.message}
            </Typography>
            {error.stack && (
              <Box
                component="pre"
                sx={{
                  m: 0,
                  mt: 1,
                  fontFamily: 'monospace',
                  fontSize: '0.75rem',
                  whiteSpace: 'pre-wrap',
                }}
              >
                {error.stack}
              </Box>
            )}
          </Box>
        )}

        <Stack direction="row" spacing={1} justifyContent="center" sx={{ mt: 5 }}>
          {reset && (
            <Button size="large" variant="contained" onClick={() => reset()}>
              Försök igen
            </Button>
          )}
          <Button size="large" variant="outlined" onClick={() => window.location.reload()}>
            Ladda om sidan
          </Button>
          {referens && (
            <Button
              size="large"
              variant="outlined"
              color="inherit"
              onClick={() => {
                setFel(hämtaSenasteFel());
                setDialogÖppen(true);
              }}
            >
              Rapportera
            </Button>
          )}
        </Stack>
      </Container>

      <FelrapportDialog
        öppen={dialogÖppen}
        fel={fel}
        vidStängning={() => setDialogÖppen(false)}
      />
    </SimpleLayout>
  );
}
